import React from 'react';
import styles from './GeographicInsights.module.css';

export default function GeographicInsights({ data, locationStats }) {
    // Sort and process location data for better insights
    const sortedLocations = data ? [...data].sort((a, b) => b.count - a.count) : [];
    const topLocations = sortedLocations.slice(0, 10);
    
    // Group by country and state/region
    const locationGroups = {};
    const countryTotals = {};
    
    // Process all sorted locations for country totals, not just top 10
    sortedLocations.forEach(item => {
        const location = item.location;
        if (!location) return;
        
        const country = location.country || 'Unknown';
        const region = location.state || location.city || 'Unknown';
        
        // Country totals
        countryTotals[country] = (countryTotals[country] || 0) + item.count;
        
        // Regional breakdown
        if (!locationGroups[country]) {
            locationGroups[country] = {};
        }
        locationGroups[country][region] = (locationGroups[country][region] || 0) + item.count;
    });
    
    // Get top countries for display
    const topCountries = Object.entries(countryTotals)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10);
    
    const formatLocation = (location) => {
        if (!location) return 'Unknown Location';
        return `${location.city}, ${location.state || location.country}`;
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>Geographic Insights</h3>
                <div className={styles.headerStats}>
                    <div className={styles.total}>
                        {topCountries.length} countries
                    </div>
                    {locationStats?.scansWithoutLocation > 0 && (
                        <div className={styles.unknownBadge}>
                            {locationStats.scansWithoutLocation} unknown locations
                        </div>
                    )}
                </div>
            </div>

            {/* Top City and Country Grid - Flipped */}
            <div className={styles.topInsights}>
                <div className={styles.topCity}>
                    <div className={styles.insightHeader}>
                        <i className="fa-solid fa-location-dot"></i>
                        <span>Top City</span>
                    </div>
                    <div className={styles.insightName}>
                        {topLocations.length > 0 ? formatLocation(topLocations[0].location) : 'No Data'}
                    </div>
                    <div className={styles.insightScans}>
                        {topLocations.length > 0 ? topLocations[0].count : 0} scans
                    </div>
                </div>

                <div className={styles.topCountry}>
                    <div className={styles.insightHeader}>
                        <i className="fa-solid fa-flag"></i>
                        <span>Top Country</span>
                    </div>
                    <div className={styles.insightName}>
                        {topCountries.length > 0 ? topCountries[0][0] : 'No Data'}
                    </div>
                    <div className={styles.insightScans}>
                        {topCountries.length > 0 ? topCountries[0][1] : 0} scans
                    </div>
                </div>
            </div>

            {/* Top Cities List */}
            <div className={styles.locationSection}>
                <div className={styles.sectionHeader}>
                    <i className="fa-solid fa-location-dot"></i>
                    <span className={styles.sectionTitle}>Top Cities</span>
                </div>
                
                <div className={styles.scrollableList}>
                    {topLocations.slice(0, 10).map((location, index) => {
                        const maxScans = topLocations[0]?.count || 1;
                        const percentage = Math.round((location.count / maxScans) * 100);
                        
                        return (
                            <div key={`city-${index}`} className={styles.locationItem}>
                                <div className={styles.locationRank}>
                                    #{index + 1}
                                </div>
                                
                                <div className={styles.locationInfo}>
                                    <div className={styles.locationName}>
                                        {formatLocation(location.location)}
                                    </div>
                                    <div className={styles.locationDetails}>
                                        {location.count} scan{location.count !== 1 ? 's' : ''}
                                    </div>
                                </div>
                                
                                <div className={styles.locationBar}>
                                    <div 
                                        className={styles.locationBarFill}
                                        style={{ 
                                            width: `${percentage}%`,
                                            backgroundColor: '#161d2d'
                                        }}
                                    />
                                </div>
                                
                                <div className={styles.locationCount}>
                                    {location.count}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {topLocations.length === 0 && (
                    <div className={styles.noDataSmall}>
                        No city data available
                    </div>
                )}
            </div>

            {/* Top Countries List */}
            <div className={styles.locationSection}>
                <div className={styles.sectionHeader}>
                    <i className="fa-solid fa-flag"></i>
                    <span className={styles.sectionTitle}>Top Countries</span>
                </div>
                
                <div className={styles.scrollableList}>
                    {topCountries.map(([country, count], index) => {
                        const maxScans = topCountries[0]?.[1] || 1;
                        const percentage = Math.round((count / maxScans) * 100);
                        
                        return (
                            <div key={`country-${index}`} className={styles.locationItem}>
                                <div className={styles.locationRank}>
                                    #{index + 1}
                                </div>
                                
                                <div className={styles.locationInfo}>
                                    <div className={styles.locationName}>
                                        {country}
                                    </div>
                                    <div className={styles.locationDetails}>
                                        {count} scan{count !== 1 ? 's' : ''}
                                    </div>
                                </div>
                                
                                <div className={styles.locationBar}>
                                    <div 
                                        className={styles.locationBarFill}
                                        style={{ 
                                            width: `${percentage}%`,
                                            backgroundColor: '#161d2d'
                                        }}
                                    />
                                </div>
                                
                                <div className={styles.locationCount}>
                                    {count}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {topCountries.length === 0 && (
                    <div className={styles.noDataSmall}>
                        No country data available
                    </div>
                )}
            </div>

            {(!data || data.length === 0) && (
                <div className={styles.noData}>
                    <i className="fa-solid fa-globe" style={{ fontSize: '2rem', color: '#d1d5db', marginBottom: '1rem' }}></i>
                    <p>No geographic data available.</p>
                </div>
            )}
        </div>
    );
} 